import {Icon, NewButton, Pagination, Search} from 'letrus-ui';
import {Link, useHistory} from 'react-router-dom';
import Tooltip from 'react-tooltip';
import {ChangeEvent, useEffect, useMemo, useState} from 'react';
import NoDataIllustration from 'images/no-data.svg';
import LoadingTable from 'components/LoadingComponents/LoadingTable';
import DeletionModal from 'components/DeletionModal';
import {
  useDeleteTextMarkerCategory,
  useFetchTextMarkerCategoriesLazy,
} from 'store/reducers/textMarkerCategories';
import FeedbackModal from 'components/FeedbackModal';
import Select from 'react-select';
import entitiesPerPageOptions from 'utils/entitiesPerPageOptions';
import SelectOption from 'utils/types/SelectOption';
import {AuthRoutes} from 'routes';
import PageWrapper from '../../components/PageWrapper';
import styles from './TextMarkerCategoryList.module.scss';

const TextMarkerCategoryList: React.FC = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [numberOfCategoriesPerPage, setNumberOfCategoriesPerPage] =
    useState<SelectOption | null>({label: '10', value: '10'});
  const [
    isDeleteTextMarkerCategoryModalOpen,
    setIsDeleteTextMarkerCategoryModalOpen,
  ] = useState<boolean>(false);
  const [textMarkerCategoryToDeleteId, setTextMarkerCategoryToDeleteId] =
    useState<number>(0);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] =
    useState<boolean>(false);

  const [
    fetchGrammarRulesLazy,
    {
      isFetching: isFetchingTextMarkerCategories,
      isLoading: isLoadingTextMarkerCategories,
      isUninitialized: isFetchTextMarkerCategoriesUninitialized,
      data,
    },
  ] = useFetchTextMarkerCategoriesLazy();
  const [
    deleteTextMarkerCategory,
    {
      isLoading: isDeletingGrammarRule,
      isSuccess: isDeleteSuccess,
      isError: isDeleteError,
    },
  ] = useDeleteTextMarkerCategory();

  const history = useHistory();

  useEffect(() => {
    fetchGrammarRulesLazy(
      {
        search: searchText,
        limit: numberOfCategoriesPerPage?.value,
        offset: (currentPage - 1) * Number(numberOfCategoriesPerPage?.value),
      },
      true,
    );
  }, [currentPage, numberOfCategoriesPerPage?.value]);

  useEffect(() => {
    if (isDeleteError) {
      setIsFeedbackModalOpen(true);
    }
  }, [isDeleteError]);

  useEffect(() => {
    if (isDeleteSuccess) {
      setIsFeedbackModalOpen(true);
    }
  }, [isDeleteSuccess]);

  useEffect(() => {
    if (isDeleteSuccess && !isDeletingGrammarRule) {
      fetchGrammarRulesLazy({}, true);
    }
  }, [isDeleteSuccess, isDeletingGrammarRule]);

  const totalNumberOfPages = useMemo(
    () =>
      data?.total
        ? Math.ceil(data.total / Number(numberOfCategoriesPerPage?.value))
        : 0,
    [data],
  );

  const isLoading =
    isFetchingTextMarkerCategories || isLoadingTextMarkerCategories;
  const hasFirstTextMarkerCategoriesFetchHappened =
    !isFetchTextMarkerCategoriesUninitialized;

  function onChangeSearchText(event: ChangeEvent<HTMLInputElement>) {
    if (!event.target.value || event.target.value === '') {
      fetchGrammarRulesLazy(
        {limit: numberOfCategoriesPerPage?.value, offset: 0},
        true,
      );

      setCurrentPage(1);
    }

    const inputValue = event.target.value.toLowerCase();

    setSearchText(inputValue);
  }

  function onClickDeleteTextMarkerCategory(textMarkerCategory: number) {
    setTextMarkerCategoryToDeleteId(textMarkerCategory);
    setIsDeleteTextMarkerCategoryModalOpen(true);
  }

  function onDeleteTextMarkerCategory() {
    deleteTextMarkerCategory({
      textMarkerCategoryId: textMarkerCategoryToDeleteId,
    });
    setIsDeleteTextMarkerCategoryModalOpen(false);
  }

  function onSubmitSearch() {
    setCurrentPage(1);

    fetchGrammarRulesLazy(
      {
        search: searchText,
        limit: numberOfCategoriesPerPage?.value,
        offset: (currentPage - 1) * Number(numberOfCategoriesPerPage?.value),
      },
      true,
    );
  }

  function resetData() {
    setCurrentPage(1);
    setSearchText('');

    fetchGrammarRulesLazy(
      {
        limit: numberOfCategoriesPerPage?.value,
        offset: (currentPage - 1) * Number(numberOfCategoriesPerPage?.value),
      },
      true,
    );
  }

  return (
    <PageWrapper>
      <div className={styles.container}>
        <header>
          <h1>Categorias de marcador</h1>

          <NewButton
            text="Criar categoria"
            kind="primary"
            userRole="teacher"
            type="button"
            onClick={() => history.push(AuthRoutes.textMarkerCategoryCreation)}
          />
        </header>

        {(isLoading || isDeletingGrammarRule) && (
          <div className={styles.loadingWrapper}>
            <LoadingTable />
          </div>
        )}

        {!isLoading &&
          hasFirstTextMarkerCategoriesFetchHappened &&
          !data?.results?.length && (
            <div className={styles.noResults}>
              <img
                src={NoDataIllustration}
                alt="Ilustração de sem resultados"
              />
              <h2>Sem resultados</h2>

              <div className={styles.resetDataButtonWrapper} title="Recarregar">
                <NewButton icon={['fas', 'undo']} onClick={resetData} />
              </div>
            </div>
          )}

        {!isLoading && data?.results?.length && !isDeletingGrammarRule && (
          <div className={styles.tableWrapper}>
            <div className={styles.searchContainer}>
              <Search
                onClick={onSubmitSearch}
                id="name"
                search={{
                  name: 'search',
                  value: searchText,
                  onChange: onChangeSearchText as (
                    eventOrValue: string | React.SyntheticEvent<Element, Event>,
                  ) => void,
                  // TODO: Type onChange correctly on UI
                }}
                searchProps={{
                  placeholder: 'Pesquisar categoria',
                  searchTag: 'input',
                  required: false,
                }}
              />

              <div
                data-tip=""
                data-for="numberOfEntitiesPerPageSelect"
                data-testid="numberOfEntitiesPerPageSelectWrapper"
                title="numberOfEntitiesPerPageSelectWrapper"
              >
                <Select
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: 100,
                      '&:hover': {borderColor: '#5d3d85'},
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#95989A',
                      paddingRight: 12,
                    }),
                    indicatorSeparator: () => ({}),
                  }}
                  onChange={(event) => setNumberOfCategoriesPerPage(event)}
                  options={entitiesPerPageOptions}
                  value={numberOfCategoriesPerPage}
                  inputId="entityEvaluationVersion"
                  placeholder="Selecione a versão da regra"
                  name="entityEvaluationVersion"
                />
              </div>

              <Tooltip
                className={styles.numberOfEntitiesPerPageSelectTooltip}
                id="numberOfEntitiesPerPageSelect"
                type="dark"
                effect="solid"
                place="top"
              >
                <div className={styles.wrapper}>
                  <p>Número de categorias por página</p>
                </div>
              </Tooltip>
            </div>

            <table>
              <thead>
                <tr>
                  <th title="ID">ID</th>
                  <th title="Nome">Nome</th>
                  <th title="Categoria">Varname</th>
                  <th title="Ações">Ações</th>
                </tr>
              </thead>

              <tbody>
                {data?.results?.map((item) => (
                  <tr key={item.id}>
                    <td title={String(item.id)}>{item.id}</td>
                    <td title={item.name}>{item.name}</td>
                    <td title={item.varname}>{item.varname}</td>
                    <td>
                      <div className={styles.buttonWrapper}>
                        <Link
                          to={`/categorias-marcador/${item.id}`}
                          title="Ir aos detalhes da categoria"
                        >
                          <Icon
                            icon={['fas', 'external-link']}
                            color="#666"
                            size="lg"
                          />
                        </Link>

                        <button
                          type="button"
                          className={styles.deleteButton}
                          title="Excluir categoria"
                          onClick={() =>
                            onClickDeleteTextMarkerCategory(item.id)
                          }
                        >
                          <Icon
                            icon={['fas', 'trash-alt']}
                            color="#666"
                            size="lg"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className={styles.paginationWrapper}>
              <Pagination
                currentPage={currentPage}
                hasNext={currentPage < totalNumberOfPages}
                hasPrevious={currentPage > 1}
                totalPages={totalNumberOfPages}
                onChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        )}
      </div>

      <DeletionModal
        isOpen={isDeleteTextMarkerCategoryModalOpen}
        message="Deseja mesmo excluir essa categoria?"
        onClickDelete={onDeleteTextMarkerCategory}
        onClose={() => setIsDeleteTextMarkerCategoryModalOpen(false)}
        subtitle="Tem certeza?"
        title="Excluir categoria"
      />

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        title="Excluir categoria de marcador"
        subtitle={isDeleteError ? 'Erro!' : 'Sucesso!'}
        feedbackMessage={
          isDeleteError
            ? 'Ocorreu um erro ao excluir a categoria, tente novamente.'
            : 'A categoria foi excluída.'
        }
        onClose={() => setIsFeedbackModalOpen(false)}
        onButtonClick={() => setIsFeedbackModalOpen(false)}
        buttonText={isDeleteError ? 'Tentar novamente' : 'Voltar'}
        iconName={isDeleteError ? 'undo' : 'arrow-left'}
      />
    </PageWrapper>
  );
};

export default TextMarkerCategoryList;
